// src/components/landing/Features.jsx
import React from 'react';

const Features = () => {
  const features = [
    {
      icon: '',
      title: 'AI-Powered Summarization',
      description: 'Transform lengthy notes into concise summaries using advanced AI algorithms. Save time and focus on what matters.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: '',
      title: 'Smart Flashcard Generator',
      description: 'Automatically generate flashcards from your notes for efficient studying and revision sessions.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: '',
      title: 'Advanced Search',
      description: 'Search through all your notes by content, tags, or context. Find exactly what you need in seconds.',
      color: 'from-green-500 to-teal-500',
    },
    {
      icon: '',
      title: 'Progress Analytics',
      description: 'Track your learning progress, note-taking habits, and study patterns with detailed analytics.',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: '☁️',
      title: 'Sync Across Devices',
      description: 'Access your notes anywhere, anytime. Automatic sync across all your devices.',
      color: 'from-indigo-500 to-blue-500',
    },
    {
      icon: '🔒',
      title: 'Secure & Private',
      description: 'Bank-level encryption ensures your notes are safe and private. Your data belongs to you.',
      color: 'from-gray-800 to-gray-900',
    },
    {
      icon: '🎯',
      title: 'Smart Tags & Categories',
      description: 'Automatically categorize and tag your notes for better organization and retrieval.',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: '🤝',
      title: 'Collaboration Tools',
      description: 'Share notes, collaborate with teams, and work together in real-time (Pro feature).',
      color: 'from-pink-500 to-rose-500',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Everything You Need for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Smart Note-Taking
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Packed with powerful features to enhance your learning and productivity
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              {/* Gradient Background Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300`} />
              
              <div className="relative z-10">
                <div className={`text-4xl mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
              
              {/* Hover Effect Line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-2xl`} />
            </div>
          ))}
        </div>

        {/* Feature Comparison */}
        <div className="mt-24 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-3xl p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose IntelliNote AI?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Compared to traditional note-taking apps, we offer intelligent features that actually save you time
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-6xl mb-4">⏱️</div>
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">10x Faster</h4>
              <p className="text-gray-600 dark:text-gray-300">
                Summarize notes 10x faster than manual review
              </p>
            </div>
            <div className="text-center">
              <div className="text-6xl mb-4">🎯</div>
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">95% Accuracy</h4>
              <p className="text-gray-600 dark:text-gray-300">
                AI-powered features with 95% accuracy rate
              </p>
            </div>
            <div className="text-center">
              <div className="text-6xl mb-4">💸</div>
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Save with yearly billing</h4>
              <p className="text-gray-600 dark:text-gray-300">
                Compared to hiring a research assistant
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;